import { TableComponent } from '../../ui/TableComponent';
import { useMemo, useEffect, useState } from 'react';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';
import { ResponsiveTable, MobileCardList, MobileCardField } from '../../shared/mobile/ResponsiveTable';
import { currencyFormatter } from '../../../services/helperService';
import { primaryText, semiBoldText } from '../../../styling/stylingHelper';
import { cn } from '../../../lib/utils';
import { TopSection } from '../../ui/TopSection';
import { useRevenueHistory } from '../../../stores/appRevenueHistoryStore';
import { useAppUserDefaultStore, saveVatSetting } from '../../../stores/appUserDefaultStore';
import { useFiltersStore } from '../../../stores/appFiltersStore';
import { filterRevenueByDateRange } from '../../../utils/dateRangeFilters';
import { calculateTotalRevenue } from '../../../utils/revenueHistoryCalculations';
import { filterRevenueByRestaurantSections, getAvailableCategories, getTotalRevenue, toDate, getAvailableOverallTypeOptions, filterRevenueByOverallType, getAvailableRevenueSourceNames } from '../../revenuehistory/utils';
import { filterAllRevenue } from '../../../utils/filterUtils';
import { useTimeControls } from '../../../hooks/useTimeControls';
import { useNavigate } from 'react-router-dom';
import { useLoadRevenueHistory } from '../../../utils/dataLoadingUtils';
import { StatCard } from '../../ui/StatCard';
import { FileText } from 'lucide-react';
import { iconBgBlue } from '../../../styling/stylingHelper';
import { metricIcon } from '../../ui/metricIcons';
import { RevenueCategory } from '../../../services/revenuehistory/revenueHistoryService';
import { getTableFormattedDate } from '../../../utils/dateUtils';
import { useRevenueFilters } from '../../../hooks/useRevenueFilters';
import { resetScrollToTop, restoreScrollPosition, saveCurrentScrollPosition } from '../../../utils/scrollUtils';
import { isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useTranslation } from 'react-i18next';


// Elevated card surface for the mobile card list (matches the shipped RevenueHistoryPage cards).
const MOBILE_CARD =
  'bg-white dark:bg-card-dark rounded-xl p-4 shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]';

// How many mobile cards to reveal per "Load more" tap (never render the whole list at once).
const MOBILE_BATCH = 20;

const RevenueHistoryRevenuePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Reset scroll to top when this page loads
  useEffect(() => {
    resetScrollToTop();
  }, []);

  // Restore scroll position when returning from detail page
  useEffect(() => {
    restoreScrollPosition();
  }, []);

  // Load data
  useLoadRevenueHistory();

  // Connect to stores
  const revenueHistoryStore = useRevenueHistory();
  const showExVatRevenue = useAppUserDefaultStore(s => s.showExVat);
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const restaurants = useRestaurantStore(s => s.restaurants);
  const restaurantRevenueSources = useRestaurantStore(s => s.restaurantRevenueSources);

  // The revenue sources in scope for this page: every restaurant's on "All Restaurants",
  // otherwise just the selected one's. Feeds both the overall-type options and the venue filter.
  const pageRevenueSources = useMemo(() => (
    isAllRestaurants
      ? Object.values(restaurantRevenueSources).flat()
      : (restaurantId ? restaurantRevenueSources[restaurantId] || [] : [])
  ), [isAllRestaurants, restaurantRevenueSources, restaurantId]);


  // Get loading state from store
  const isRevenueLoading = useRevenueHistory(s => s.isLoading);

  // Mobile-only: how many record cards are rendered (grows on "Load more").
  // The desktop table paginates internally; on mobile we window the same array.
  const [mobileVisible, setMobileVisible] = useState<number>(MOBILE_BATCH);

  // Use centralized filter store
  const overallTypeFilter = useFiltersStore(s => s.overallTypeFilter);
  const setOverallTypeFilter = useFiltersStore(s => s.setOverallTypeFilter);
  const sectionFilters = useFiltersStore(s => s.sectionFilters);
  const restaurantFilters = useFiltersStore(s => s.restaurantFilters);
  const setRestaurantFilters = useFiltersStore(s => s.setRestaurantFilters);

  // Revenue filters hook - only revenue categories for this page
  const {
    filters,
    setRevenueCategoryFilters,
    setRevenueSourceFilters,
    resetFilters: resetRevenueFilters,
    activeFilterCount: baseActiveFilterCount,
  } = useRevenueFilters({
    includeRevenueCategories: true,
    includeRevenueSources: true,
    includeMenuCategories: false,
  });

  // Calculate total active filter count including restaurant filters
  const activeFilterCount = useMemo(() => {
    let count = baseActiveFilterCount;
    if (isAllRestaurants && restaurantFilters.length > 0) {
      count++;
    }
    return count;
  }, [baseActiveFilterCount, isAllRestaurants, restaurantFilters]);

  // Wrapper function to reset all filters including restaurant filters
  const resetFilters = () => {
    resetRevenueFilters();
    if (isAllRestaurants) {
      setRestaurantFilters([]);
    }
  };

  // Time controls - centralized logic
  const {
    startDate,
    endDate,
    selectedTimeOption,
    dateRange,
    setDateRange,
    setSelectedTimeOption,
  } = useTimeControls();

  // Filter data by date range
  const dateFilteredRevenue = useMemo(() =>
    filterRevenueByDateRange(revenueHistoryStore.revenueHistoryList, startDate, endDate),
    [revenueHistoryStore.revenueHistoryList, startDate, endDate]
  );

  // Handler for overall type filter changes
  const handleOverallTypeChange = (value: string) => {
    setOverallTypeFilter(value as 'all' | 'food' | 'drinks' | 'take-away');
  };

  // Handler for VAT toggle - updates both orders and revenue VAT settings
  const handleVatChange = async (value: string) => {
    const showExVat = value === 'excl';
    try {
      // Update both settings in parallel
      await Promise.all([
        saveVatSetting(showExVat),
      ]);
    } catch (error) {
      console.error('Failed to save VAT settings:', error);
    }
  };

  // Apply overall type filter, then section filters, then revenue filters (revenue categories only)
  const overallTypeFilteredRevenue = useMemo(() =>
    filterRevenueByOverallType(dateFilteredRevenue, overallTypeFilter),
    [dateFilteredRevenue, overallTypeFilter]
  );

  const sectionFilteredRevenue = useMemo(() =>
    filterRevenueByRestaurantSections(overallTypeFilteredRevenue, sectionFilters),
    [overallTypeFilteredRevenue, sectionFilters]
  );

  const filteredRevenue = useMemo(() =>
    filterAllRevenue(
      sectionFilteredRevenue,
      filters,
      undefined,
      undefined,
      isAllRestaurants ? restaurantFilters : undefined,
      pageRevenueSources
    ),
    [sectionFilteredRevenue, filters, isAllRestaurants, restaurantFilters, pageRevenueSources]
  );

  // Calculate total revenue
  const totalRevenue = useMemo(() =>
    calculateTotalRevenue(filteredRevenue, showExVatRevenue),
    [filteredRevenue, showExVatRevenue]
  );

  // Convert revenue data to table format
  const revenueTableData = useMemo(() => {
    return filteredRevenue.map((rev) => {
      const total = getTotalRevenue(rev, showExVatRevenue);
      const itemCount = rev.revenueData.categories.reduce(
        (count: number, cat: RevenueCategory) => count + cat.items.length,
        0
      );

      return {
        date: getTableFormattedDate(toDate(rev.date)),
        source: rev.revenueSource,
        sourceType: rev.revenueSourceType || '-',
        revenue: total,
        items: itemCount,
        id: rev.id, // Store ID for click handling
      };
    });
  }, [filteredRevenue, showExVatRevenue]);

  // Reset the mobile window whenever the filtered result set changes.
  useEffect(() => {
    setMobileVisible(MOBILE_BATCH);
  }, [revenueTableData]);

  const handleGoBack = () => {
    navigate("/revenuehistory")
  }

  const handleRowClick = (row: any) => {
    if (row.id) {
      // Save scroll position before navigating to detail page
      saveCurrentScrollPosition();
      navigate(`/revenuehistory/detail/${row.id}`);
    }
  };

  // Get available filter options from the section-filtered data (before applying revenue filters)
  const availableRevenueCategories = useMemo(() =>
    getAvailableCategories(sectionFilteredRevenue),
    [sectionFilteredRevenue]
  );

  // Get available filter options from restaurants (fixed list from store, not from filtered data)
  const availableRestaurants = useMemo(() =>
    isAllRestaurants ? restaurants.map(r => ({ id: r.id, name: r.name })) : [],
    [isAllRestaurants, restaurants]
  );

  // Get available overall type options (base options + dynamic revenue source types)
  const availableOverallTypeOptions = useMemo(
    () => getAvailableOverallTypeOptions(pageRevenueSources),
    [pageRevenueSources]
  );

  // Venue options for the revenue source filter (distinct source names, e.g. "Untable" / "bar POP")
  const availableRevenueSources = useMemo(
    () => getAvailableRevenueSourceNames(pageRevenueSources),
    [pageRevenueSources]
  );

  // Reset filter to 'all' if current value is not available on this page
  useEffect(() => {
    if (overallTypeFilter && !availableOverallTypeOptions.includes(overallTypeFilter)) {
      setOverallTypeFilter('all');
    }
  }, [overallTypeFilter, availableOverallTypeOptions, setOverallTypeFilter]);

  // Drop venue selections this restaurant does not have (switching restaurant keeps the
  // store-level filter). Same idea as the overall-type reset above; skipped while the sources
  // are still loading, which would otherwise wipe a legitimate selection on first render.
  useEffect(() => {
    if (availableRevenueSources.length === 0 || filters.revenueSources.length === 0) return;
    const valid = filters.revenueSources.filter(name => availableRevenueSources.includes(name));
    if (valid.length !== filters.revenueSources.length) setRevenueSourceFilters?.(valid);
  }, [availableRevenueSources, filters.revenueSources, setRevenueSourceFilters]);

  return (
    <div>
      <MobilePageHeader title={t('revenueHistory.title')} right={<RestaurantSelector />} />
      <TopSection
          title={t('revenueHistory.title')}
          backButton={true}
          handleGoBack={handleGoBack}
          dateRange={dateRange}
          setDateRange={setDateRange}
          selectedTimeOption={selectedTimeOption}
          setSelectedTimeOption={setSelectedTimeOption}
          onOverallTypeChange={handleOverallTypeChange}
          currentOverallTypeValue={overallTypeFilter}
          overallTypeOptions={availableOverallTypeOptions}
          onVatChange={handleVatChange}
          currentVatValue={showExVatRevenue ? 'excl' : 'incl'}
          showFilters={true}
          filters={{
            revenueFilters: {
              restaurantFilters: isAllRestaurants ? restaurantFilters : undefined,
              setRestaurantFilters: isAllRestaurants ? setRestaurantFilters : undefined,
              availableRestaurants: isAllRestaurants ? availableRestaurants : undefined,
              revenueCategoryFilters: filters.revenueCategories,
              setRevenueCategoryFilters,
              availableRevenueCategories,
              revenueSourceFilters: filters.revenueSources,
              setRevenueSourceFilters,
              availableRevenueSources,
              onResetFilters: resetFilters,
              activeFilterCount,
            }
          }}
        />

        {/* Responsive layout group for content sections below 1250px */}
      <div className="flex flex-col gap-6 min-[1250px]:flex-col">
        {/* KPI Cards, row on >=1250px, column below */}
        <div className="flex flex-col gap-3 sm:gap-6 min-[1250px]:flex-row">
          <div className="w-full min-[1250px]:flex-1">
          <StatCard
            {...metricIcon('revenue')}
            title={t('revenueHistory.totalRevenue')}
            value={totalRevenue}
            valueType="currency"
            loading={isRevenueLoading}
          />
          </div>
          <div className="w-full min-[1250px]:flex-1">
          <StatCard
            icon={<FileText className="w-6 h-6" style={{ color: '#2E90FA' }} />}
            iconBg={iconBgBlue}
            title={t('revenueHistory.recordsFound')}
            value={filteredRevenue.length}
            valueType="number"
            loading={isRevenueLoading}
          />
          </div>
        </div>

      {/* Responsive layout group for content sections below 1250px */}
      <div className="flex flex-col gap-6 min-[1250px]:flex-col">
        {/* Revenue records table component */}
        <div className="w-full">
            <ResponsiveTable
              desktop={
            <TableComponent
              title={t('revenueHistory.revenueRecordsTitle')}
              data={revenueTableData}
              blankLines={false}
              usePagination={true}
              firstColumn={'narrow'}
              columns={[
                { key: 'date', label: t('revenueHistory.date'), columnType: 'base' },
                { key: 'source', label: t('revenueHistory.source'), columnType: 'name'},
                { key: 'sourceType', label: t('revenueHistory.section'), columnType: 'base'},
                { key: 'items', label: t('revenueHistory.items'), columnType: 'blueNumber' },
                { key: 'revenue', label: t('revenueHistory.revenue'), columnType: 'currencyHighlight' }
              ]}
              onRowClick={handleRowClick}
            />
              }
              mobile={
                <div className="w-full">
                  <h3 className={cn('mb-4 text-[18px] leading-6', semiBoldText, primaryText)}>{t('revenueHistory.revenueRecordsTitle')}</h3>
                  <MobileCardList
                    items={revenueTableData.slice(0, mobileVisible)}
                    keyOf={(row) => row.id}
                    onLoadMore={() => setMobileVisible((v) => v + MOBILE_BATCH)}
                    hasMore={mobileVisible < revenueTableData.length}
                    loadMoreLabel={t('revenueHistory.loadMore')}
                    renderCard={(row) => (
                      <div className={cn(MOBILE_CARD, 'flex flex-col gap-3')} onClick={() => handleRowClick(row)}>
                        <div className="flex items-center justify-between gap-2">
                          <span className={cn('text-[14px] leading-5 font-medium capitalize truncate min-w-0', primaryText)}>{row.source}</span>
                          <span className={cn('text-base font-bold flex-shrink-0', primaryText)}>{currencyFormatter.format(row.revenue)}</span>
                        </div>
                        <div className="flex items-stretch">
                          <MobileCardField label={t('revenueHistory.date')} value={row.date} divider />
                          <MobileCardField label={t('revenueHistory.section')} value={row.sourceType} divider />
                          <MobileCardField label={t('revenueHistory.items')} value={String(row.items)} />
                        </div>
                      </div>
                    )}
                  />
                </div>
              }
            />
          </div>
      </div>
    </div>
    </div>
  );
};

export default RevenueHistoryRevenuePage;
